import { useContext, useState } from 'react'
import Image from 'next/image'
import styled, { css } from 'styled-components'
import { BookMark, Close } from 'svgIcons'
import {
  AnalysisResult_AnalysisReportFragment,
  GqlAnalysisReportsDocument,
  GqlAnalysisReportsQuery,
  GqlUserScrapCountsDocument,
  GqlViewerScrappedAnalysisReportsDocument,
  useGqlDeleteAnalysisReportScrapMutation,
} from 'generated/graphql'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { getLimitNumText } from 'components/ActivityListCardItem/module/getLimitNumText'
import LoginAlert from 'components/LoginAlert'
import { useCurrentUserContext } from 'hooks/useCurrentUser'
import useFlutterInappwebview from 'hooks/useFlutterInappwebview'
import { imageLoader, optimizeImageUrl } from 'utils/optimizeImageUrl'
import { ANALYSIS_RESULT_ITEM_LOGO_SIZE } from '../constant/logoImageSize'
import { AnalysisReportsContext } from '../context/AnalysisReportsContext'
import useIncreaseCount from '../hooks/useIncreaseCount'

interface IFProps {
  item: AnalysisResult_AnalysisReportFragment
  isScrapScreen?: boolean
}

const AnalysisMainResultItem = ({ item, isScrapScreen = false }: IFProps) => {
  const [showLoginAlert, setShowLoginAlert] = useState<boolean>(false)
  const isWebview = useIsWebview()
  const { fluterInappwebview } = useFlutterInappwebview()
  const { user } = useCurrentUserContext()
  const { state } = useContext(AnalysisReportsContext)

  const { updateAnalysisReportScrap } = useIncreaseCount()
  const [deleAnalysisReportScrap] = useGqlDeleteAnalysisReportScrapMutation()

  const closeLoginAlert = () => {
    setShowLoginAlert(false)
  }

  const handleClickBookmark = () => {
    if (!user) {
      setShowLoginAlert(true)
      return
    }
    updateAnalysisReportScrap({
      variables: {
        input: {
          analysisReportId: item.id,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateAnalysisReportScrap: {
          scrapped: !item.viewerScrapped,
          __typename: 'UpdateAnalysisReportScrapPayload',
        },
      },
      update: (cache) => {
        const cachedData = cache.readQuery<GqlAnalysisReportsQuery>({
          query: GqlAnalysisReportsDocument,
          variables: state,
        })
        cache.writeQuery({
          query: GqlAnalysisReportsDocument,
          variables: state,
          data: {
            ...cachedData,
            analysisReports: {
              __typename: 'AnalysisReportConnection',
              totalCount: cachedData?.analysisReports.totalCount,
              nodes: cachedData?.analysisReports.nodes.map((node) => {
                if (node.id === item.id) {
                  return {
                    ...node,
                    viewerScrapped: !item.viewerScrapped,
                    scrapCount: item.viewerScrapped
                      ? node.scrapCount - 1
                      : node.scrapCount + 1,
                  }
                }
                return node
              }),
            },
          },
        })
      },
    })
  }
  const handleClickDelete = () => {
    deleAnalysisReportScrap({
      variables: {
        analysisReportId: item.id,
      },
      refetchQueries: [
        GqlViewerScrappedAnalysisReportsDocument,
        GqlUserScrapCountsDocument,
      ],
      onCompleted: () => {
        if (isWebview) {
          fluterInappwebview?.callHandler('reportRemoveScrap')
        }
      },
    })
  }

  return (
    <StyledWrapper>
      <Image
        alt={item.title}
        width={ANALYSIS_RESULT_ITEM_LOGO_SIZE}
        height={ANALYSIS_RESULT_ITEM_LOGO_SIZE}
        objectFit="contain"
        loader={imageLoader}
        src={optimizeImageUrl({
          url: item.thumbnailImage.url,
          width: 'auto',
          height: ANALYSIS_RESULT_ITEM_LOGO_SIZE,
        })}
      />

      <a
        className="item-info"
        href={`/company-analysis/${item.id}`}
        target="_blank"
        rel="noreferrer"
      >
        <>
          <p className="company-name">{item.title}</p>
          <dl className="item-count">
            <dt>조회수</dt>
            <dd>
              {getLimitNumText({
                number: item.impressionCount,
                limit: 9999,
              })}
            </dd>
            {isScrapScreen && (
              <>
                <dt>스크랩 수</dt>
                <dd>
                  {getLimitNumText({
                    number: item.scrapCount,
                    limit: 9999,
                  })}
                </dd>
              </>
            )}
          </dl>
        </>
      </a>
      {isScrapScreen ? (
        <button type="button" className="close-btn" onClick={handleClickDelete}>
          <Close width={15} height={15} />
        </button>
      ) : (
        <button
          type="button"
          className="scrap-btn"
          onClick={handleClickBookmark}
        >
          <BookMark
            isOn={item.viewerScrapped}
            className="bookmark-icon"
            strokeType="#666666"
            color="#01a0ff"
          />
          <p className="scrap-count">{item.scrapCount}</p>
        </button>
      )}
      <LoginAlert
        DialogProps={{ open: showLoginAlert }}
        onRequestClose={closeLoginAlert}
      />
    </StyledWrapper>
  )
}

export default AnalysisMainResultItem

const StyledWrapper = styled.li`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 24px;
    height: 92px;
    border-bottom: 1px solid ${theme.background.bg_1};

    &:first-child {
      border-top: 1px solid ${theme.background.bg_1};
    }
    .item-info {
      flex: 1;
      cursor: pointer;
      overflow: hidden;
      .company-name {
        font-size: 16px;
        margin-bottom: 4px;
        overflow: hidden;
        text-overflow: ellipsis;
        word-wrap: break-word;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .item-count {
        font-size: 12px;
        font-weight: normal;
        display: flex;
        align-items: center;
        dt:nth-of-type(2)::before {
          content: '';
          display: inline-block;
          width: 1px;
          height: 8px;
          margin: 0 8px;
          background: ${theme.line.line_1};
        }
        dd {
          margin-left: 4px;
        }
      }
    }

    > button {
      width: 28px;
      cursor: pointer;
      background: none;
      padding: 0px;
      border: none;
    }
    .scrap-btn {
      width: 40px;
      display: flex;
      flex-direction: column;
      align-items: center;
      flex-shrink: 0;
      gap: 5px;
      padding-top: 10px;
      cursor: pointer;

      > p {
        font-size: 12px;
        color: ${theme.color.text_2};
      }
    }
  `}
`
